import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const TOTAL_AMOUNT_FALLBACK_KEYS = ['totalAmountDue', 'amountExcludingLateFees', 'currentCharges', 'billAmount', 'totalAmount', 'amount'];

/**
 * The amount to allocate for the ONE service being worked on: the subtotal of the utility being
 * allocated, on the property whose allocation is open.
 *
 * Scoped by both, because neither alone names a service. A bill charging two properties for the same
 * utility carries a subtotal for each, so matching on the utility alone returns whichever block the
 * provider happened to print first — the other property's money, every time it is not this one's.
 *
 * @param {object|null} data Extracted bill payload.
 * @param {{propertyId?: string, utilityId?: string, utilityTypeName?: string}} [scope]
 *   Which service to read. Omit entirely for a whole-bill view, which has no one service to narrow to.
 *
 * Preference order:
 *   1. the service whose resolved `utilityId` matches, within the block for `propertyId`
 *   2. within that same block, the service whose `serviceType` reads as `utilityTypeName`
 *   3. the bill's top-level total, for a payload that resolved no per-service subtotal at all
 */
export function getExtractedTotalAmount(data) {
  var _ref;
  let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!data) return null;
  const propertyId = scope.propertyId,
    utilityId = scope.utilityId,
    utilityTypeName = scope.utilityTypeName;
  const blocks = Array.isArray(data.properties) ? data.properties : [];

  // Blocks a payload never named a property for are kept: on those the single block IS the property,
  // and dropping them would lose the subtotal for every extraction made before ids were stamped.
  const inScope = propertyId ? blocks.filter(block => !(block !== null && block !== void 0 && block.propertyId) || block.propertyId === propertyId) : blocks;
  const services = inScope.flatMap(block => Array.isArray(block === null || block === void 0 ? void 0 : block.services) ? block.services : []);
  const byUtilityId = utilityId ? services.find(service => (service === null || service === void 0 ? void 0 : service.utilityId) === utilityId) : undefined;
  const byTypeName = utilityTypeName ? services.find(service => String((service === null || service === void 0 ? void 0 : service.serviceType) || '').toLowerCase() === String(utilityTypeName).toLowerCase()) : undefined;
  const subtotal = Number((_ref = byUtilityId !== null && byUtilityId !== void 0 ? byUtilityId : byTypeName) === null || _ref === void 0 ? void 0 : _ref.subtotal);
  if (Number.isFinite(subtotal)) return subtotal;
  const key = TOTAL_AMOUNT_FALLBACK_KEYS.find(k => data[k] !== undefined && data[k] !== null);
  const fallback = key ? Number(data[key]) : null;
  return Number.isFinite(fallback) ? fallback : null;
}

/**
 * The total the provider is to be paid for the whole bill file, for the AP push.
 *
 * Distinct from {@link getExtractedTotalAmount}, which narrows to the one utility being allocated:
 * when a bill covers several utilities or properties the extractor narrows the top-level totals to
 * the matched slice and records the untouched figures under `invoiceTotals` (with `wasNarrowed`).
 * The amount owed to the provider is the whole invoice, so it is read from there, and only falls
 * back to the top-level total for extractions made before `invoiceTotals` existed.
 */
export function getExtractedInvoiceTotal(data) {
  var _data$invoiceTotals;
  if (!data) return null;
  const candidates = [(_data$invoiceTotals = data.invoiceTotals) === null || _data$invoiceTotals === void 0 ? void 0 : _data$invoiceTotals.totalAmountDue, data.totalAmountDue];
  const found = candidates.find(value => Number.isFinite(Number(value)) && value !== null && value !== '');
  return found === undefined ? null : Number(found);
}

/** Strip leading [ErrorCode] from messages such as "[ServiceProviderNotFoundForProperty] None of the…". */
export function formatBracketedAutomationError(message) {
  const s = String(message).trim();
  const match = s.match(/^\[([^\]]+)]\s*(.+)$/);
  if (match) return match[2].trim();
  return s;
}

/** Flatten + de-duplicate user-facing strings from a list of candidate fields on the response. */
function collectMessageList(candidates) {
  const seen = new Set();
  const result = [];
  for (const c of candidates) {
    if (c == null) continue;
    const arr = Array.isArray(c) ? c : [c];
    for (const entry of arr) {
      let text = '';
      if (typeof entry === 'string') text = formatBracketedAutomationError(entry);else if (entry && typeof entry === 'object' && typeof entry.message === 'string') text = entry.message.trim();else if (entry != null) text = String(entry).trim();
      if (text && !seen.has(text)) {
        seen.add(text);
        result.push(text);
      }
    }
  }
  return result;
}

/** Pull human-readable messages from bill automation API error payloads. */
export function getBillAutomationErrorList(response) {
  var _data$clientContextVa;
  if (!response || typeof response !== 'object') return [];
  const data = response.data || {};
  return collectMessageList([response.errors, response.errorMessages, data.errors, data.errorMessages, typeof response.error === 'string' ? [response.error] : response.error,
  // Client-context rejections from the bill automation pipeline are blocking, treat them as errors
  data.clientRejectionMessage, data.clientRejectionMessages, (_data$clientContextVa = data.clientContextValidation) === null || _data$clientContextVa === void 0 ? void 0 : _data$clientContextVa.requiredUtilitiesRejectionMessage]);
}

/** Pull non-blocking warning messages from the bill automation API response. */
export function getBillAutomationWarningList(response) {
  var _data$clientContextVa2;
  if (!response || typeof response !== 'object') return [];
  const data = response.data || {};
  return collectMessageList([response.warnings, response.warningMessages, data.warnings, data.warningMessages, (_data$clientContextVa2 = data.clientContextValidation) === null || _data$clientContextVa2 === void 0 ? void 0 : _data$clientContextVa2.addressMismatchWarning]);
}

/**
 * Whether apply billing may delete a bill it recovered for the cycle on screen.
 *
 * It may only when this screen is what uploaded it, for this one utility. `uploadedForUtilityId` is
 * what decides that: apply billing is the only channel that sets it, and it holds exactly one
 * utility, so a match means "uploaded here, for what is open now". A bill that arrived by email or
 * through the Bills page has none, and a bill uploaded here for a different utility will not match.
 *
 * The source is checked alongside rather than instead of it, so the two would have to disagree
 * before a bill this screen does not own could be deleted from it.
 */
export function canDeleteRecoveredBill(bill, utilityId) {
  if (!(bill !== null && bill !== void 0 && bill.billId) || !utilityId) return false;
  return bill.uploadedForUtilityId === utilityId && bill.source === 'Manual';
}

/** Why the delete control is disabled, said in terms of what the person can do instead. */
export function getRecoveredBillDeleteHint(canDelete) {
  return canDelete ? 'Delete this bill' : 'This bill was not uploaded here for this utility. Delete it from the Bills page.';
}

/**
 * How to introduce a bill this screen did not just receive, as a heading and a sentence.
 *
 * Only a bill uploaded HERE is something the person left unfinished — saying "you left off" about a
 * bill that arrived by email, or one somebody else uploaded on the Bills page, would be telling them
 * about a session they never had. The channel also decides what they can do with it next, so it is
 * named rather than left for them to work out from a disabled control.
 */
export function getRecoveredBillIntro(bill) {
  switch (bill === null || bill === void 0 ? void 0 : bill.source) {
    case 'Manual':
      return {
        title: 'We picked up where you left off',
        body: 'You uploaded this bill here for this utility and closed the screen before finishing, so we ' + 'brought it back rather than asking you to upload it again.'
      };
    case 'Portal':
      return {
        title: 'A bill for this cycle is already on file',
        body: 'It was uploaded on the Bills page and covers this property and utility, so we brought it in ' + 'rather than asking you to upload it again.'
      };
    case 'Email':
      return {
        title: 'A bill for this cycle arrived by email',
        body: 'It covers this property and utility, so we brought it in rather than asking you to upload it ' + 'again.'
      };
    case 'Scrape':
      return {
        title: 'A bill for this cycle was collected for you',
        body: 'It was downloaded from the provider portal and covers this property and utility, so we ' + 'brought it in rather than asking you to upload it again.'
      };
    case 'Integration':
      return {
        title: 'A bill for this cycle came from your provider',
        body: 'It arrived through a provider integration and covers this property and utility, so we ' + 'brought it in rather than asking you to upload it again.'
      };
    default:
      return {
        title: 'A bill for this cycle is already on file',
        body: 'It covers this property and utility, so we brought it in rather than asking you to upload it again.'
      };
  }
}

/** What the person can do about the bill, which is not the same question on every channel. */
export function getRecoveredBillDeleteNote(canDelete) {
  return canDelete ? 'If you would rather start over, delete it with the delete icon at the top of this window.' : 'It covers more than this utility, so it cannot be deleted from here — use the Bills page.';
}

/**
 * The extraction narrowed to the one service being allocated: the property whose allocation is open,
 * and within it the utility on screen.
 *
 * The summary and the line items are about THIS allocation, so showing every property the bill covers
 * — and every service on them — asks a person to find their own row in someone else's data. The
 * unnarrowed payload is still what the AP push and the "additional utilities" count read, because
 * those are about the whole invoice.
 */
export function narrowExtractionToScope(data) {
  let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!data) return data;
  const propertyId = scope.propertyId,
    utilityId = scope.utilityId,
    utilityTypeName = scope.utilityTypeName;
  const blocks = Array.isArray(data.properties) ? data.properties : [];
  if (!blocks.length) return data;

  // A block the extraction never named a property for is kept: there the single block IS the property.
  const forProperty = propertyId ? blocks.filter(block => !(block !== null && block !== void 0 && block.propertyId) || block.propertyId === propertyId) : blocks;
  const properties = (forProperty.length ? forProperty : blocks).map(block => {
    const services = Array.isArray(block === null || block === void 0 ? void 0 : block.services) ? block.services : [];
    const matched = services.filter(service => utilityId && service !== null && service !== void 0 && service.utilityId ? service.utilityId === utilityId : utilityTypeName ? String((service === null || service === void 0 ? void 0 : service.serviceType) || '').toLowerCase() === String(utilityTypeName).toLowerCase() : true);

    // Never narrow a block down to nothing: a service nobody resolved a utility for is still the
    // bill's, and hiding it would leave the card blank with no way to tell why.
    return _objectSpread(_objectSpread({}, block), {}, {
      services: matched.length ? matched : services
    });
  });
  return _objectSpread(_objectSpread({}, data), {}, {
    properties
  });
}